
import React from 'react';
import { 
  FormField, 
  FormItem, 
  FormLabel, 
  FormControl,
  FormMessage,
  FormDescription
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '@/components/ui/select';
import { UseFormReturn } from 'react-hook-form';
import { FarmFormValues } from './FormSchema';
import { useGetProducts } from '@/hooks/use-products';

interface FarmerInfoFieldsProps {
  form: UseFormReturn<FarmFormValues>;
}

const FarmerInfoFields: React.FC<FarmerInfoFieldsProps> = ({ form }) => {
  const { data: products = [], isLoading: isLoadingProducts } = useGetProducts();

  const farmCategories = [
    'Corporate Farm',
    'Large Scale Farmer',
    'Medium Scale Farmer',
    'Small Holder Farmer'
  ];

  return (
    <>
      <h3 className="text-xl font-semibold mt-8 mb-4">Farmer Information</h3>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <FormField
          control={form.control}
          name="farmer_name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Farmer Name</FormLabel>
              <FormControl>
                <Input placeholder="Enter farmer's name" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        
        <FormField
          control={form.control}
          name="phone_number"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Phone Number</FormLabel>
              <FormControl>
                <Input placeholder="e.g. +251 91 234 5678" {...field} value={field.value || ''} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        
        <FormField
          control={form.control}
          name="union_name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Union Name</FormLabel>
              <FormControl>
                <Input placeholder="Enter farmer's union" {...field} value={field.value || ''} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        
        <FormField
          control={form.control}
          name="officer"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Officer Name</FormLabel>
              <FormControl>
                <Input placeholder="Enter field officer name" {...field} value={field.value || ''} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        
        <FormField
          control={form.control}
          name="farm_category"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Farm Category</FormLabel>
              <Select 
                onValueChange={field.onChange} 
                defaultValue={field.value}
                required
              >
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select farm category" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {farmCategories.map((category) => (
                    <SelectItem key={category} value={category}>
                      {category}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />
        
        <FormField
          control={form.control}
          name="product_name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Product</FormLabel>
              <Select 
                onValueChange={field.onChange} 
                defaultValue={field.value || ''}
                required
                disabled={isLoadingProducts}
              >
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder={isLoadingProducts ? "Loading products..." : "Select a product"} />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {products.map((productName) => (
                    <SelectItem key={productName} value={productName}>
                      {productName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />
        
        <FormField
          control={form.control}
          name="distance_from_addis"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Distance from Addis Ababa (km)</FormLabel>
              <FormControl>
                <Input 
                  type="number" 
                  min="0" 
                  placeholder="Distance in kilometers" 
                  {...field} 
                  onChange={e => field.onChange(parseFloat(e.target.value))}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        
        <FormField
          control={form.control}
          name="city"
          render={({ field }) => (
            <FormItem>
              <FormLabel>City</FormLabel>
              <FormControl>
                <Input placeholder="Enter city name" {...field} value={field.value || ''} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        
        <FormField
          control={form.control}
          name="area"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Area</FormLabel>
              <FormControl>
                <Input placeholder="Enter area/district name" {...field} value={field.value || ''} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
        <FormField
          control={form.control}
          name="has_contract"
          render={({ field }) => (
            <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
              <div className="space-y-0.5">
                <FormLabel className="text-base">Contract Status</FormLabel>
                <FormDescription>
                  Does this farmer have a signed contract?
                </FormDescription>
              </div>
              <FormControl>
                <Switch
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              </FormControl>
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="has_phone_access"
          render={({ field }) => (
            <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
              <div className="space-y-0.5">
                <FormLabel className="text-base">Phone Access</FormLabel>
                <FormDescription>
                  Does the farmer have access to a phone?
                </FormDescription>
              </div>
              <FormControl>
                <Switch
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              </FormControl>
            </FormItem>
          )}
        />
      </div>
    </>
  );
};

export default FarmerInfoFields;
